import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Auth } from '@aws-amplify/auth';

import { AuthAppMessageEvent } from '../types/message';
import TokensService from '../../../../shared/services/TokensService';
import { readRedirectedFromParam } from '../helpers/functions/url';
import LocalStorage from '../../../../shared/services/LocalStorage';
import SessionStorage from '../../../../shared/services/SessionStorage';

/**
 * Custom hook to handle Cognito tokens received from the auth app via postMessage
 */
export default function useTokensBroker({
  defaultRedirectUrl,
}: {
  defaultRedirectUrl: string;
}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tokensService = useMemo(
    () => new TokensService(LocalStorage.getInstance()),
    [],
  );

  useEffect(() => {
    const authAppUrl = new URL(import.meta.env.VITE_AUTH_APP_URL);

    const handleMessage = (event: AuthAppMessageEvent) => {
      if (event.origin !== authAppUrl.origin) {
        return;
      }

      const redirectedFrom = readRedirectedFromParam(searchParams);

      switch (event.data.type) {
        case 'TOKENS': {
          tokensService.storeTokens(event.data.payload.tokens);
          void navigate(redirectedFrom ?? defaultRedirectUrl, {
            replace: true,
          });

          break;
        }

        case 'FEDERATED_SIGN_IN': {
          const sessionStorage = SessionStorage.getInstance();
          const { provider } = event.data.payload;

          if (redirectedFrom) {
            sessionStorage.setItem('redirectedFrom', redirectedFrom);
          }

          void Auth.federatedSignIn({ customProvider: provider });

          break;
        }

        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [defaultRedirectUrl, navigate, searchParams, tokensService]);
}
